import React, { useMemo } from 'react';
import cx from 'classnames';
import {
  formatToIconName,
  IconName,
  StreamingIconTreeNodeData,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import type { MentionItem } from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionsPlugin';
import { resolveHref } from './resolveHref';

type IconProps = StreamingIconTreeNodeData & {
  id: string;
  editorClassName?: string;
  mentionItems?: MentionItem[];
  overrideVariables?: Partial<Record<string, string>>;
  isEditor?: boolean;
};

export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  (
    {
      id,
      className,
      editorClassName,
      name,
      href,
      openInNewTab = true,
      overrideVariables,
      mentionItems: _mentionItems,
      isEditor = false,
      ...props
    },
    ref,
  ) => {
    const [IconComponent, setIconComponent] = React.useState<React.ComponentType<any> | null>(null);
    const iconModuleName = name ? formatToIconName(name) : undefined;

    React.useEffect(() => {
      if (!iconModuleName) {
        setIconComponent(null);
        return;
      }

      const loadIcon = async () => {
        try {
          const iconModule = await import('@heroicons/react/24/outline');
          const Component = iconModule[iconModuleName as IconName];
          setIconComponent(() => Component);
        } catch (error) {
          console.warn(`Failed to load icon: ${iconModuleName}`, error);
        }
      };

      loadIcon();
    }, [iconModuleName]);

    const iconClassName = cx(className, editorClassName);

    const resolvedHref = useMemo(() => resolveHref(href, overrideVariables), [href, overrideVariables]);

    const iconElement = !IconComponent ? (
      <svg id={id} ref={ref} className={iconClassName} width="24" height="24" {...props} />
    ) : (
      <IconComponent id={id} ref={ref} className={iconClassName} width="24" height="24" {...props} />
    );

    if (resolvedHref) {
      return (
        <a href={resolvedHref} target={openInNewTab ? '_blank' : '_self'} rel="noreferrer">
          {iconElement}
        </a>
      );
    }

    return iconElement;
  },
);

Icon.displayName = 'Icon';
